import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import toastConfig from "../../../../../components/shared/constants/toaster-config";
import { TEMPLATE_TYPE } from "../constants/template";
import { createTemplate as createTemplateUtility } from "../../../../../utils/broadcast";
import { isTemplateBackendError, resolveTemplateErrorMessage } from "../helpers/broadcast-error-messages";

// `resetKey` and `isCurrent` are optional (F3): a caller that seeds a
// current-selection gate (see resume-broadcast.jsx) can reset this hook's
// stale loading/error/success on File/media/channel/company context change
// and ignore completions after unmount or after a newer attempt started.
export const useTemplate = ({ resetKey } = {}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isTemplateCreated, setIsTemplateCreated] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => () => { mountedRef.current = false; }, []);

  useEffect(() => {
    if (resetKey === undefined) return;
    setIsLoading(false);
    setError(null);
    setIsTemplateCreated(false);
  }, [resetKey]);

  const channelId = useSelector((state) => state.campaign.create.channelId);
  const companyId = useSelector((state) => state.user.data?.company?.id);

  const formatTemplateType = (type) => {
    if (!type && typeof type !== 'string') {
      return TEMPLATE_TYPE.TEXT;
    }

    if (type.startsWith("image/")) {
      return TEMPLATE_TYPE.IMAGE;
    } 

    if (type.startsWith("video/")) {
      return TEMPLATE_TYPE.VIDEO;
    } 

    if (type.startsWith("application/pdf")) {
      return TEMPLATE_TYPE.DOCUMENT;
    } 

    return TEMPLATE_TYPE.TEXT;
  }
  
  const createTemplate = async (
    {
      templateName,
      templateCategory,
      bodyMessage,
      templateFile,
      templateFileUrl,
      templateVariables,
      templateButtons,
      footerMessage,
    },
    isCurrent = () => true,
  ) => {
    const ERROR_MESSAGE = "Error al crear la plantilla, contacte a soporte";

    setIsTemplateCreated(false);
    setError(null);

    if (!companyId) {
      setError(ERROR_MESSAGE)
      toast.error(ERROR_MESSAGE , toastConfig);
      return null;
    }

    if (!channelId) {
      setError(ERROR_MESSAGE)
      toast.error(ERROR_MESSAGE , toastConfig);
      return null;
    }

    try {
      const templateData = {
        name: templateName,
        category: templateCategory,
        lang: 'es',
        text: bodyMessage,
        type: formatTemplateType(templateFile?.type),
        variables: templateVariables ?? [],
        mediaUrl: templateFileUrl,
        buttons: templateButtons,
        ...(footerMessage && { footer: footerMessage })
      };

      setIsLoading(true);

      const templateResult = await createTemplateUtility(companyId, channelId, templateData);

      if (!mountedRef.current || !isCurrent()) return templateResult;
      setIsTemplateCreated(true);

      return templateResult;
    } catch (error) {
      const { message, isExpected, diagnostics = {} } = resolveTemplateErrorMessage(error);

      if (!isExpected) {
        const tags = { component: "BroadcastSelectFile", op: "createTemplate" };
        if (!isTemplateBackendError(error) && error instanceof Error) {
          Sentry.captureException(error, { tags });
        } else {
          Sentry.captureException(new Error("Template creation backend failure"), {
            tags,
            extra: diagnostics,
          });
        }
      }

      if (!mountedRef.current || !isCurrent()) return null;
      toast.error(message, toastConfig);
      setError(message);

      return null;
    } finally {
      if (mountedRef.current && isCurrent()) setIsLoading(false);
    }
  }

  return {
    createTemplate,
    isLoading,
    error,
    isTemplateCreated,
  }
}